import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useFieldArray } from "react-hook-form";

import {
  useApiErrors,
  useHookForm,
  useAdminApiQueryWithParams,
  useAdminApiMutation,
  useNotifications,
  useReactRouterLink,
  useDebounce,
} from "../../../hooks";
import {
  CreateInterProcessorSingleTransfer,
  IMoveFundsAcrossProcessors,
  IFormWithTableProps,
  IHookForm,
  IViewBatchTransfer,
  IViewTopUpWalletBankAccount,
  NoMeta,
  PagedMeta,
  IPageAndSearchRequest,
} from "../../../interfaces-and-types";
import {
  generateRandomString,
  getCurrentDateFormatted,
  globalHandlerUtils,
} from "../../../utils";

import { View } from "./View";
import { batchBankTransferSchema } from "./moveFundsValidationSchema";

const isDevMode = process.env.NODE_ENV === "development";

const ALLOWED_EXTENSIONS = ["csv"];

export type TBatchExcelOrSingleTransfers =
  | "singleBankTransfers"
  | "singleGatewayTransfers"
  | "bankExcelUpload";

interface IGatewayAndPageSelect extends IPageAndSearchRequest {
  // country: string;
  // currency: string;
  gateway_code: string;
}

export interface IMoveFundsFormViewProps
  extends IHookForm<IMoveFundsAcrossProcessors>, IFormWithTableProps {
  isLoadingMoveFunds: boolean;
  isLoadingPgwTopUpBankDetails: boolean;

  closeModal: () => void;
  // submitForm: () => void;
  formValues: IMoveFundsAcrossProcessors;
  isDirty: boolean;

  // destination bank details list
  pgwTopUpBankDetails: IViewTopUpWalletBankAccount[];

  // bank transfers
  // bankTransfersFieldArray: IMoveFundsAcrossProcessors["shadow_bank_transfers"];
  // appendNewBankTransfer: (
  //   newBankTransferFormValues: CreateInterProcessorSingleTransfer
  // ) => void;
  // removeBankTransferByIndex: (index: number) => void;
  // updateBankTransferByIndex: (
  //   index: number,
  //   updatedTransfer: CreateInterProcessorSingleTransfer
  // ) => void;
}

const generateTrsfRef = (): string =>
  `SWIP-TRSF-${getCurrentDateFormatted()}-${generateRandomString(15)}`;

const alertTransferFailed = () =>
  alert("The funds could not be moved! Please retry...");

export const MoveFundsForm = (props: IFormWithTableProps) => {
  const { navigate, navigateToRoutePreserveParams } = useReactRouterLink();
  const location = useLocation();

  const notificationsHandler = useNotifications();

  const {
    isSuccess: isSuccessMoveFunds,
    isLoading: isLoadingMoveFunds,
    isError: isErrorMoveFunds,
    mutate: moveFunds,
  } = useAdminApiMutation<IMoveFundsAcrossProcessors, IViewBatchTransfer>(
    "moveFunds",
    ["listInterProcessorSingleTransfers"],
    {
      timeout: 1000 * 60 * 10, // 10 mins timeout to allow for batch verification
    },
  ).usePostMutation;

  useEffect(() => {
    if (isSuccessMoveFunds) {
      closeModal();
      reset(); // clear form
      props.refreshTable();
    }
  }, [isSuccessMoveFunds]);

  useEffect(() => {
    if (isErrorMoveFunds) {
      closeModal();
      reset(); // clear form
      props.refreshTable();
      alertTransferFailed();
    }
  }, [isErrorMoveFunds]);

  const initialSingleFormValue: CreateInterProcessorSingleTransfer = {
    reference: generateTrsfRef(),
    description: null,
    currency: "NGN",
    amount: 0,
    top_up_wallet_details_id: "",
    account_number: "",
    source_gateway_code: "",
    recipient_gateway_code: "",
    bank_name: "",
    account_name: "",
  };

  const initialBatchFormValues: IMoveFundsAcrossProcessors = {
    batch_reference: `SWIP-BATCH-${getCurrentDateFormatted()}-${generateRandomString(
      15,
    )}`,
    title: null,
    description: null,
    source_currency: "NGN",
    shadow_bank_transfers: [initialSingleFormValue],
    shadow_gateway_transfers: [],
  };

  const handleApiSubmit = (data: IMoveFundsAcrossProcessors) => {
    moveFunds(data);
  };

  const handleError = (errors: any) => {
    globalHandlerUtils.handleError(errors);

    // if (!isValid && isDevMode)
    //   alert("Form is invalid: " + JSON.stringify(errors)); // stringify is causing circular JSON errors

    if (!isValid) {
      notificationsHandler.showNotification({
        ariaLabel: "error notification",
        title: "WARNING",
        kind: "error",
        message:
          "NOTE: Invalid details supplied! The funds could not be moved.",
        statusIconDescription: "error",
        timeout: 10000,
      });
    }
  };

  const {
    onFormSubmit,
    errors,
    register,
    reset,
    control,
    watch,
    setValue,
    isDirty,
    isValid,
  } = useHookForm({
    initialFormValues: initialBatchFormValues,
    validationSchema: batchBankTransferSchema,
    onValid: handleApiSubmit,
    onInvalid: handleError,
  });

  const formValues = watch();

  // BANK TRANSFERS FIELD ARRAY
  // const {
  //   fields: bankTransfers,
  //   append: appendBankTransfer,
  //   remove: removeBankTransfer,
  //   update: updateBankTransfer,
  //   // replace: replaceBankTransfer,
  // } = useFieldArray({ name: "shadow_bank_transfers", control });

  // const appendNewBankTransfer = (
  //   newSingleBankTransferFormValues: CreateInterProcessorSingleTransfer
  // ) => {
  //   appendBankTransfer(newSingleBankTransferFormValues);

  //   if (formValues.source_currency === "")
  //     setValue("source_currency", newSingleBankTransferFormValues.currency);
  // };

  // const removeBankTransferByIndex = (index: number) => {
  //   if (
  //     window.confirm(
  //       "Are you sure you want to remove this recipient from the list?"
  //     )
  //   )
  //     removeBankTransfer(index);
  // };

  // const updateBankTransferByIndex = (
  //   index: number,
  //   updatedTransfer: CreateInterProcessorSingleTransfer
  // ) => {
  //   if (
  //     window.confirm(
  //       "Are you sure you want to update transfer details for this recipient?"
  //     )
  //   )
  //     updateBankTransfer(index, {
  //       ...updatedTransfer,
  //     });
  // };

  // const bankTransferCountry = "NG"; // TODO: provide method of updating this value
  const debouncePeriodElapsed = useDebounce();

  const {
    isFetching: isFetchingPgwTopUpBankDetails,
    isLoading: isLoadingPgwTopUpBankDetails,
    data: pgwTopUpBankDetails,
  } = useAdminApiQueryWithParams<
    IGatewayAndPageSelect,
    IViewTopUpWalletBankAccount[],
    PagedMeta
  >(
    [
      "listProcessorTopUpWalletBankDetails",
      {
        // country: bankTransferCountry,
        gateway_code:
          formValues.shadow_bank_transfers[0].recipient_gateway_code,
        page: 1,
        page_size: 100,
        search: null,
      },
    ],
    {
      enabled:
        // debouncePeriodElapsed &&
        // !!formValues.country &&
        !!formValues.shadow_bank_transfers[0].recipient_gateway_code &&
        !!formValues.source_currency,
    },
  );

  // const submitForm = (): boolean => {
  //   onFormSubmit();

  //   if (!isValid && isDevMode)
  //     alert("Form is invalid: " + JSON.stringify(errors));

  //   if (!isValid) {
  //     notificationsHandler.showNotification({
  //       ariaLabel: "error notification",
  //       title: "WARNING",
  //       kind: "error",
  //       message:
  //         "NOTE: Invalid details supplied! The funds could not be moved.",
  //       statusIconDescription: "error",
  //       timeout: 10000,
  //     });

  //     return false;
  //   }

  //   return true;
  // };

  const firstEntry = formValues.shadow_bank_transfers[0];

  // always clear the top-up wallet id once the gateway code changes
  useEffect(() => {
    reset({
      ...formValues,
      shadow_bank_transfers: [
        {
          ...firstEntry,
          top_up_wallet_details_id: "",
        },
      ],
    });
  }, [firstEntry.recipient_gateway_code]);

  const selectedTopUpAccount = pgwTopUpBankDetails?.find(
    (acc) => acc.id === firstEntry.top_up_wallet_details_id,
  );

  useEffect(() => {
    reset({
      ...formValues,
      shadow_bank_transfers: [
        {
          ...firstEntry,
          account_name: selectedTopUpAccount?.account_name,
          account_number: selectedTopUpAccount?.account_number,
          bank_name: selectedTopUpAccount?.bank_name,
        },
      ],
    });
  }, [selectedTopUpAccount?.id]);

  const closeModal = () =>
    navigate(location.pathname.replace("/move-funds", ""));

  return (
    <View
      {...props}
      isLoadingMoveFunds={isLoadingMoveFunds}
      isLoadingPgwTopUpBankDetails={isFetchingPgwTopUpBankDetails}
      closeModal={closeModal}
      // others
      // submitForm={submitForm}
      control={control}
      formValues={formValues}
      isDirty={isDirty}
      pgwTopUpBankDetails={pgwTopUpBankDetails || []}
      // bank transfer field array
      // bankTransfersFieldArray={bankTransfers}
      // appendNewBankTransfer={appendNewBankTransfer}
      // removeBankTransferByIndex={removeBankTransferByIndex}
      // updateBankTransferByIndex={updateBankTransferByIndex}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
